import {useArtworkCreated} from '../artwork-created/useArtworkCreated.js'
import {ArtworkProgress} from '../artwork-created/ArtworkProgress.jsx'
import RecordedChampion from '../../components/RecordedChampion.jsx'
/* THE FACE OF A WORK ROW - shared by the Command Center Work queue and My
 * Worklist, so the two surfaces cannot disagree about who won or who buys.
 *
 * Brian, 2026-10-26: "command center - working queue does not show champion.
 * it just shows an account" / "work list doesn't include who the champion is
 * either." Model and measurements: championFace.js.
 *
 * Order on the card is the order a rep reads it:
 *   WHAT (championship, labelled parts) -> WHO WON (team row, or ONE indy-ring
 *   list of athletes with their schools) -> WHO WE SELL TO (account) -> WHY
 *   it is on this list.
 * Phone-first: everything wraps, nothing is clipped or `nowrap`, no fixed
 * widths. Labels are words, not colour alone. */
import { formatDate } from '../../lib/championships.js'
import { championFace, championshipFace } from './championFace.js'
import styles from './WorkFace.module.css'

export default function WorkFace({ record, reason = '', face = null, showWhat = true }) {
  const artworkRead = useArtworkCreated(record?.sourceType || record?.isIndyGroup ? null : record?.id)
  const artworkRows=artworkRead.rows?.length?artworkRead.rows:artworkRead.row?[artworkRead.row]:[]
  const savedArtwork = artworkRows.some(row=>row.artwork?.state==='saved')
  const what = championshipFace(record)
  const f = face || championFace(record)
  const parts = [
    what.name && { k: 'Championship', v: what.name },
    what.body && { k: 'Body', v: what.body },
    what.sport && { k: 'Sport', v: what.sport },
    what.classLabel && { k: 'Class', v: what.classLabel },
    what.state && { k: 'State', v: what.state },
    what.date && { k: 'Date', v: formatDate(what.date) },
  ].filter(Boolean)
  return (
    <div className={styles.face} data-champion-type={f.type}>
      {showWhat && parts.length > 0 && (
        <p className={styles.what} aria-label="Championship">
          {parts.map((p) => (
            <span key={p.k} className={styles.part}><span className={styles.k}>{p.k}</span> {p.v}</span>
          ))}
        </p>
      )}

      <RecordedChampion record={record} showNext={!savedArtwork} />
      <div className={styles.who} data-testid="work-champion">
        <p className={styles.whoHead}>
          <span aria-hidden="true" className={styles.glyph}>{f.titleTier === 'ring_tier' ? '♛' : '•'}</span>
          <span className={styles.k}>{f.headline}</span>
          {f.type === 'team' && (
            <span className={styles.team}>{f.team}{f.year ? ` ${f.year}` : ''}</span>
          )}
        </p>
        {(f.type === 'individual' || f.type === 'multi') && f.athletes.length > 0 && (
          <ul className={styles.athletes} aria-label={f.titleTier === 'ring_tier' ? 'Individual champions' : 'Individual winners'}>
            {f.athletes.map((a, i) => (
              <li key={`${a.athlete}-${a.school}-${i}`} className={styles.athlete}>
                <span className={styles.athleteName}>{a.athlete || 'Athlete not named yet'}</span>
                {a.school && <span className={styles.athleteSchool}>{a.school}</span>}
                {a.event && <span className={styles.athleteEvent}>{a.event}</span>}
              </li>
            ))}
          </ul>
        )}
        {f.policyLabel && <p className={styles.note}>{f.policyLabel}{f.policyReason ? ` · ${f.policyReason}` : ''}</p>}
        {f.note && <p className={styles.note}>{f.note}</p>}
      </div>

      <p className={styles.account} data-testid="work-account">
        <span className={styles.k}>Account</span>{' '}
        {f.account
          ? <span className={styles.accountName}>{f.account}</span>
          : <span className={styles.gap}>{f.accountNote || 'Not known yet.'}</span>}
      </p>

      {artworkRows.map(row=><ArtworkProgress key={row.jobId} row={row} compact />)}
      {['error','conflict'].includes(artworkRead.state) && <p role="status" className={styles.note}>Artwork status unavailable; saved artwork may exist. Refresh the read rather than recreate it.</p>}
      {reason && !savedArtwork && (
        <p className={styles.why} data-testid="work-reason">
          <span className={styles.k}>Why it is here</span> {reason}
        </p>
      )}
    </div>
  )
}
